import { TYPE } from '@kontentino/kontentino-constants/Pages';
import SelectNewProfileType from 'components/modals/newSocialMediaProfile/SelectNewProfileType';
import SelectSocialProfiles from 'components/modals/newSocialMediaProfile/selectSocialProfiles/SelectSocialProfiles';
import Modal from 'components/shared/Modal';
import React, { FC, useEffect, useState } from 'react';
import { ModalRouteProps } from 'context/ModalRouter';
import { ActivityIndicator, Button } from '@kontentino/ui';
import AnalyticsApi from 'api/analytics';
import { useApolloClient } from '@apollo/client';
import { QueryKey } from 'components/insights/constants';
import { useMutation, useQueryClient } from 'react-query';
import SocialLoginApi from 'app/modules/socialLogin/api';
import { SocialLoginPage, SocialLoginTokenType } from 'types/SocialLogin';
import { ApiClientError } from 'api/client';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import { useTranslation } from 'react-i18next';
import PageTypeUtils from 'app/utils/pageType';
import SelectFacebookTokenType from 'components/modals/newSocialMediaProfile/SelectFacebookTokenType';
import useMetaTokenTypeStep from 'components/modals/newSocialMediaProfile/hooks/useMetaTokenTypeStep';

type Props = ModalRouteProps & {};

const availableTypes = [TYPE.FACEBOOK, TYPE.INSTAGRAM, TYPE.LINKEDIN];

export const InsightsAddNewProfileModal: FC<Props> = ({ modalManager }) => {
  const { t } = useTranslation();
  const socialLogin = useSocialLoginContext();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const { mutate: mutateLoadPages, isLoading: isLoadPagesLoading } =
    useMutation(AnalyticsApi.loadPages, {
      onSuccess: () => {
        apolloClient.resetStore();
        queryClient.resetQueries(QueryKey.initialization());
        modalManager.close();
      },
    });
  const { mutate: mutatePagesToSocialLogin, isLoading: isAddPagesLoading } =
    useMutation<
      any,
      ApiClientError,
      {
        pageType: number;
        data: {
          pages: SocialLoginPage[];
        };
      }
    >(
      (variables) =>
        SocialLoginApi.addPagesToSocialLogin(
          variables.pageType,
          variables.data,
        ),
      {
        onSuccess: () => {
          mutateLoadPages();
        },
      },
    );

  const proceedAuthorize = (
    pageType: number,
    tokenType?: SocialLoginTokenType,
  ) => {
    socialLogin.authorize({
      pageType,
      tokenType,
    });
  };

  const metaTokenTypeStep = useMetaTokenTypeStep(proceedAuthorize, null);

  const proceedAuthorizeObo = () => {
    proceedAuthorize(
      metaTokenTypeStep.selectedPageType ?? TYPE.FACEBOOK,
      'system_user_obo',
    );
  };

  useEffect(() => {
    return () => {
      socialLogin.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authorizedPages: SocialLoginPage[] = socialLogin.isSuccess
    ? socialLogin.authorization?.data ?? []
    : [];
  const isSelectingProfiles = authorizedPages.length > 0;

  const handleSelect = (id: string) => {
    setSelectedProfiles((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSubmit = () => {
    if (!socialLogin.pageType || selectedProfiles.length === 0) {
      return;
    }

    const pages = authorizedPages.filter((page) =>
      selectedProfiles.includes(page.external_id),
    );

    mutatePagesToSocialLogin({
      pageType: socialLogin.pageType,
      data: { pages },
    });
  };

  const isBusy =
    socialLogin.isLoading || isLoadPagesLoading || isAddPagesLoading;
  const isChoosingMethod =
    !isBusy && !isSelectingProfiles && metaTokenTypeStep.isChoosingTokenType;

  return (
    <Modal
      modalManager={modalManager}
      size="responsive"
      dataName={{
        wrapper: 'insights_add-profile-modal',
        closeButton: 'insights_add-profile-modal_close-icon',
      }}
      classNames={{ modal: '!tw-max-w-[440px]' }}
    >
      <Modal.Header
        title={
          isChoosingMethod
            ? t('newSocialMediaProfile.methodCards.title')
            : t('insights.addProfileModal.title')
        }
        onGoBack={
          metaTokenTypeStep.isChoosingTokenType
            ? metaTokenTypeStep.reset
            : undefined
        }
      />
      <Modal.Content className="tw-mb-6">
        {!isBusy && isSelectingProfiles && (
          <>
            <SelectSocialProfiles
              profiles={authorizedPages.map((profile) => ({
                id: profile.external_id,
                name: profile.name,
                logo: profile.logo ? { src: profile.logo } : undefined,
                type: PageTypeUtils.getNumberFromName(profile.type),
                businessId: profile.business_id,
                businessName: profile.business_name,
              }))}
              selectedIds={selectedProfiles}
              isPartial={socialLogin.authorization?.partial}
              onSelect={(id) => handleSelect(id as string)}
              onSelectNone={() => setSelectedProfiles([])}
            />
            <Modal.Footer className="tw-px-0">
              <Button
                disabled={selectedProfiles.length === 0}
                onClick={handleSubmit}
                data-name="insights_add-profile-modal_confirm"
                className="tw-w-full"
              >
                {t('addProfile')}
              </Button>
            </Modal.Footer>
          </>
        )}
        {isChoosingMethod && (
          <SelectFacebookTokenType
            onSelectPersonal={metaTokenTypeStep.handleSelectPersonal}
            onSelectObo={proceedAuthorizeObo}
          />
        )}
        {!isBusy &&
          !isSelectingProfiles &&
          metaTokenTypeStep.view === 'network' && (
            <SelectNewProfileType
              socialMediaTypes={availableTypes}
              onSelectSocialMediaType={metaTokenTypeStep.handleSelectNetwork}
              dataNamePrefix={{
                socialTypeOption: 'insights_add-profile-modal_',
              }}
            />
          )}
        {isBusy && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6">
            <ActivityIndicator className="tw-h-12 tw-w-12" />
            <p className="tw-mt-6 tw-text-md">
              {t('insights.addProfileModal.authorizing')}
            </p>
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};
